import _ from 'lodash';

import host from '../utils/getHost';
import { getSelfRegInstallerId } from '../utils/pricing';
import { getDisplayedCloudDays } from '../utils/plan';

import { privatePaymentServerRequest, publicPaymentServerRequest } from './managers/requestBuilder';
import { getUid } from './managers/store';
import { getUserLocation } from './user';

// Prices for self reg users
// If user is logged in, response will default to their price set in db unless `getPricesForLoggedInUser` is false
// Otherwise just returns standard prices for self reg installer
export const getPricePlans = async (getPricesForLoggedInUser = true) => {
    let params;

    const uid = getPricesForLoggedInUser ? getUid() : null;
    // If no uid, then this is a non-logged in user so use Videoloft installer
    // Otherwise user is logged in so pass uid
    if (!uid) {
        const userLocation = await getUserLocation();
        params = {
            installerId: getSelfRegInstallerId(userLocation),
            countryCode: (userLocation).country_code,
        };
    } else {
        params = {
            userId: uid,
        };
    }

    const { data } = await publicPaymentServerRequest(
        'GET',
        '/users/prices',
        {
            params,
        }
    );

    const { prices, accountLevels } = getPricesAndAccountLevelsFromPricingArray(
        data.prices,
        true
    );

    return {
        currency: data.currency,
        plans: prices.default,
        accountLevels,
        taxRate: data.taxRate / 100,
    };
};

export const getAddonsPrices = async (getPricesForLoggedInUser = true) => {
    let params;

    const uid = getPricesForLoggedInUser ? getUid() : null;
    // If no uid, then this is a non-logged in user so use Videoloft installer
    // Otherwise user is logged in so pass uid
    if (!uid) {
        const userLocation = await getUserLocation();
        params = {
            installerId: getSelfRegInstallerId(userLocation),
            countryCode: (userLocation).country_code,
        };
    } else {
        params = {
            userId: uid,
        };
    }

    const { data } = await publicPaymentServerRequest(
        'GET',
        '/users/prices/addons',
        {
            params
        }
    );
    return data;
};

// Installer's prices for their customers
// Returns all currencies
export const getInstallerPrices = async ({ customerUid, allCurrencies = false, futurePriceSet = false }) => {
    const { data } = await privatePaymentServerRequest(
        'GET',
        `/installers/:installerId/prices`,
        {
            params: {
                allCurrencies,
                priceSet: futurePriceSet ? 'future' : 'current',
                userId: customerUid
            }
        },
        true,
        true
    );

    const { prices, accountLevels } =
        getPricesAndAccountLevelsFromPricingArray(
            data.prices
        );

    return {
        currency: data.currency,
        prices: allCurrencies ? prices : prices[data.currency],
        taxRate: data.taxRate / 100,
        accountLevels
    };
};

export const getInstallerPricesForAddons = async ({ customerUid, allCurrencies = false, futurePriceSet = false }) => {
    const res = await privatePaymentServerRequest(
        'GET',
        `/installers/:installerId/prices/addons`,
        {
            params: {
                allCurrencies,
                priceSet: futurePriceSet ? 'future' : 'current',
                userId: customerUid
            }
        },
        true,
        true
    );

    return res.data;
};

// Distributor's prices for their installers
export const getDistributorPrices = async ({ allCurrencies = false, futurePriceSet = false }) => {
    const { data } = await privatePaymentServerRequest(
        'GET',
        `/distributors/:distributorId/prices`,
        {
            params: {
                allCurrencies,
                priceSet: futurePriceSet ? 'future' : 'current'
            }
        },
        true,
        true
    );

    const { prices, accountLevels } =
        getPricesAndAccountLevelsFromPricingArray(
            data.prices
        );

    return {
        currency: data.currency,
        prices: allCurrencies ? prices : prices[data.currency],
        accountLevels
    };
};

/*
    Converts array of individual prices into structured object with shape:
    {
        [currency]: {
            [resolution]: {
                [recordingMode]: {
                    [days]: {
                        accountLevel: integer,
                        hidden: boolean,
                        monthly: either [{ camLowerLimit, fee }] if `includeCamLowerLimit` is true, otherwise just the numeric fee,
                        annually: either [{ camLowerLimit, fee }] if `includeCamLowerLimit` is true, otherwise just the numeric fee
                    }
                }
            }
        }
    }

    Any price that doesn't have a specified currency will be stored under 'default'.
*/
const getPricesAndAccountLevelsFromPricingArray = (
    pricesArray,
    includeCamLowerLimit = false
) => {
    // If including cam_lower_limit, it will be structured: prices[currency][resolution][recordingMode][days] = { accountLevel, hidden, monthly: [{ camLowerLimit, fee }], annually: [{ camLowerLimit, fee }] }
    // If excluding cam_lower_limit, it will be structured: prices[currency][resolution][recordingMode][days] = { accountLevel, hidden, monthly: fee, annually: fee }
    const prices = {};

    // Collect account levels too
    const accountLevels = {};

    pricesArray.forEach(
        ({
            account_level,
            cam_lower_limit,
            currency,
            fee,
            recording_mode,
            resolution,
            sub_renewal_frequency_months,
            cloud_days,
            hidden,
        }) => {

            // If currency is undefined, then this is self reg
            // All prices will be in the currency we want (and this is the same currency as was returned in the API request)
            if (!currency) {
                currency = 'default';
            }
            
            const days = getDisplayedCloudDays(parseInt(cloud_days));

            // Record account level if haven't already
            if (!accountLevels[days]) {
                accountLevels[days] = account_level;
            }

            // Add currency to prices if not already there
            if (!prices[currency]) {
                prices[currency] = {};
            }

            // Add resolution to prices if not already there
            if (!prices[currency][resolution]) {
                prices[currency][resolution] = {};
            }

            // Add recording mode to prices if not already there
            if (!prices[currency][resolution][recording_mode]) {
                prices[currency][resolution][recording_mode] = {};
            }

            // Add cloud storage days (and matching account level) if not already there
            if (!prices[currency][resolution][recording_mode][days]) {
                prices[currency][resolution][recording_mode][days] = {
                    accountLevel: account_level,
                    hidden: !!hidden,
                };
            }

            // Now need to handle differently depending on whether including cam_lower_limit
            if (includeCamLowerLimit) {
                if (sub_renewal_frequency_months === 1) {
                    // Add monthly payment interval array if not already there
                    if (!prices[currency][resolution][recording_mode][days].monthly) {
                        prices[currency][resolution][recording_mode][days].monthly =
                            [];
                    }

                    prices[currency][resolution][recording_mode][days].monthly.push({
                        camLowerLimit: cam_lower_limit,
                        fee,
                    });
                } else if (sub_renewal_frequency_months === 12) {
                    // Add annual payment interval array if not already there
                    if (
                        !prices[currency][resolution][recording_mode][days].annually
                    ) {
                        prices[currency][resolution][recording_mode][days].annually =
                            [];
                    }

                    prices[currency][resolution][recording_mode][days].annually.push({
                        camLowerLimit: cam_lower_limit,
                        fee,
                    });
                }
            } else {
                // If not storing cam_lower_limit, now just store fee
                if (sub_renewal_frequency_months === 1) {
                    prices[currency][resolution][recording_mode][days].monthly = fee;
                } else if (sub_renewal_frequency_months === 12) {
                    prices[currency][resolution][recording_mode][days].annually = fee;
                }
            }
        }
    );

    return { prices, accountLevels };
};

export const getCloudAdapterPrices = async (countryCode) => {
    if (!countryCode) {
        countryCode = (await getUserLocation()).country_code;
    }

    const [
        {
            data: {
                currency,
                products: [cloudAdapter8],
            },
        },
        {
            data: {
                products: [cloudAdapter16],
            },
        },
        {
            data: {
                products: [cloudAdapterRack32],
            },
        },
        {
            data: {
                products: [cloudAdapterRack],
            },
        },
    ] = await Promise.all([
        publicPaymentServerRequest(
            'GET',
            `/hardware/prices/adapter8?countryCode=${countryCode}`
        ),
        publicPaymentServerRequest(
            'GET',
            `/hardware/prices/adapter16?countryCode=${countryCode}`
        ),
        publicPaymentServerRequest(
            'GET',
            `/hardware/prices/adapterrack32?countryCode=${countryCode}`
        ),
        publicPaymentServerRequest(
            'GET',
            `/hardware/prices/adapterrack?countryCode=${countryCode}`
        ),
    ]);

    return {
        currency,
        8: {
            price: cloudAdapter8.price,
            tax: cloudAdapter8.tax,
            id: cloudAdapter8.id,
        },
        16: {
            price: cloudAdapter16.price,
            tax: cloudAdapter16.tax,
            id: cloudAdapter16.id,
        },
        32: {
            price: cloudAdapterRack32.price,
            tax: cloudAdapterRack32.tax,
            id: cloudAdapterRack32.id,
        },
        64: {
            price: cloudAdapterRack.price,
            tax: cloudAdapterRack.tax,
            id: cloudAdapterRack.id,
        },
    };
};

// Buying cloud adapters
// Separate methods for logged in/out users
export const buyCloudAdaptersLoggedOut = async (
    products,
    email,
    countryCode,
    name,
    street,
    city,
    region,
    postcode,
    country,
    phone,
    discountCode
) => {
    const data = await publicPaymentServerRequest(
        'POST',
        '/hardware/orders',
        {
            data: {
                ...getCloudAdaptersPurchaseRequestBody(products, countryCode, name, street, city, region, postcode, country, phone, discountCode),
                email
            },
            headers: {
                Authorization: 'self'
            }
        }
    );

    return data;
};

export const buyCloudAdaptersLoggedIn = async (
    products,
    countryCode,
    name,
    street,
    city,
    region,
    postcode,
    country,
    phone,
    discountCode,
    paymentMethodId,
    saveCard = false,
    quoteId
) => {
    let reqData = {
        ...getCloudAdaptersPurchaseRequestBody(
            products,
            countryCode,
            name,
            street,
            city,
            region,
            postcode,
            country,
            phone,
            discountCode
        ),
        paymentMethodId,
        saveCard: saveCard
    };
    if (quoteId) {
        reqData = {
            ...reqData,
            quoteId: quoteId
        };
    }
    return await privatePaymentServerRequest(
        'POST',
        `/hardware/orders/:uid`,
        {
            data: {...reqData}
        }
    );
};

const getCloudAdaptersPurchaseRequestBody = (
    products,
    countryCode,
    name,
    street,
    city,
    region,
    postcode,
    country,
    phone,
    discountCode
) => ({
    countryCode,
    shipping: {
        name,
        street,
        city,
        region: region || undefined,
        postcode,
        country,
        phone,
    },
    products: _.map(products, (quantity, id) => ({ id, quantity })),
    host,
    discountCode
});

// Check whether a given Cloud Adapter discount code is valid
// If invalid, returns false
// If valid, returns the discount percentage (e.g. 50)
export const validateCloudAdapterDiscountCode = async (code) => {
    try {
        const { data } = await publicPaymentServerRequest(
            'GET',
            `/discountcode/adapter/${code}`
        );

        return data.discount;
    } catch {
        return false;
    }
};
